import { DefaultChatTransport, type UIMessage } from 'ai'
import { tokenManager } from '@/services/TokenManager/TokenManager'
import {
  ASSISTANT_CHAT_PATH,
  type AssistantChatRequestBody,
  type AssistantPageContext,
} from './contract'

/**
 * Reads the page the user is on when they send, so a question like "what's
 * happening with this case?" knows which case. Read per request, never cached.
 */
export type AssistantContextReader = () => AssistantPageContext

const browserTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return undefined
  }
}

/**
 * Builds the bearer header the same way the BFF axios instance does
 * (`src/axios/createAxiosInstance.ts`): a fresh token from the TokenManager,
 * refreshed first when it is about to expire.
 */
export const getAssistantAuthHeaders = async (): Promise<
  Record<string, string>
> => {
  const token = await tokenManager.getValidAccessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * The case the latest user message is about, when it came from a task
 * suggestion or briefing follow-up (its `metadata.focusCaseId`). Read per
 * request, so a retry or an approval follow-up for that question keeps it
 * and the next typed question drops it.
 */
export const latestFocusCaseId = (messages: UIMessage[]) => {
  const lastUser = [...messages].reverse().find(m => m.role === 'user')
  const metadata = lastUser?.metadata as { focusCaseId?: unknown } | undefined
  return typeof metadata?.focusCaseId === 'string'
    ? metadata.focusCaseId
    : undefined
}

/**
 * The chat transport for Ask AttorneyShare. Posts the whole (client-side)
 * history plus the page context to `/api/assistant/chat`.
 */
export const createAssistantTransport = <M extends UIMessage>(
  readContext: AssistantContextReader
) =>
  new DefaultChatTransport<M>({
    api: ASSISTANT_CHAT_PATH,
    headers: getAssistantAuthHeaders,
    prepareSendMessagesRequest: ({ messages, headers }) => {
      const focusCaseId = latestFocusCaseId(messages)
      const body: AssistantChatRequestBody = {
        messages,
        context: {
          timeZone: browserTimeZone(),
          ...readContext(),
          ...(focusCaseId ? { focusCaseId } : {}),
        },
      }
      return { body, headers }
    },
  })
